/**
 * Shared LinkedIn-style post rendering, used by Feed cards and the post preview popup.
 *
 * Every number goes through `<Num>` / `<MultiplierBadge>` (§12.5 rules 1-2): the components accept
 * envelopes only, so nothing here can put a computed value on screen. Type tags, the
 * possibly-deleted marker and the tier colours all key off server discriminants.
 */

import { useLayoutEffect, useRef, useState, type ComponentType, type ReactNode } from 'react';
import type {
  ContentType,
  EmbeddedOriginalDTO,
  MediaRefDTO,
  PostComponentDTO,
  PostMetricsDTO,
  PostMultipliersDTO,
  PostType,
} from '@lw/shared';
import {
  BookmarkIcon,
  CommentIcon,
  ExternalLinkIcon,
  MediaBlock,
  MediaGallery,
  MultiplierBadge,
  Num,
  RepostIcon,
  ThumbsUpIcon,
  cx,
} from '../../components';
import { CONTENT_TYPE_LABEL, POST_TYPE_LABEL } from '../../lib/labels';

/**
 * `typeDisplay` is the server's rendering of post type + subtype ("Original · Link share"); the
 * client prints it verbatim (§12.5) and falls back to its own label map only for rows a server
 * older than v3 produced.
 */
export function PostTypeBadges({
  postType,
  contentType,
  typeDisplay,
}: {
  postType: PostType;
  contentType: ContentType;
  typeDisplay?: string;
}) {
  return (
    <span className="post-badges">
      <span className="tag">{typeDisplay ?? POST_TYPE_LABEL[postType]}</span>
      {/* A pure repost's content type restates its post type ("Repost" · "Reposted content"), so
          the second tag adds nothing on the card — the server discriminant decides (§12.5). */}
      {contentType === 'repost' ? null : (
        <span className="tag">{CONTENT_TYPE_LABEL[contentType]}</span>
      )}
    </span>
  );
}

/** LinkedIn's post-edited marker (§7.4). */
export function EditedChip({ edited }: { edited: boolean }) {
  if (!edited) return null;
  return (
    <span className="tag" title="LinkedIn marks this post as edited after publishing.">
      Edited
    </span>
  );
}

/**
 * Graceful degradation (SPEC § graceful degradation): a subtle dot when the row carries provider
 * fields the mapper did not consume. Informational — the keys are the title, and the raw inspector
 * in the preview shows the values.
 */
export function UnmappedBadge({ keys }: { keys: string[] }) {
  if (keys.length === 0) return null;
  return (
    <span
      className="unmapped-dot"
      title={`This post carries fields the mapper does not read yet: ${keys.join(', ')}`}
      aria-label={`Unmapped fields: ${keys.join(', ')}`}
    />
  );
}

/** "Posted in <group>" — a group post is the owner's content published into a group (§7.4). */
export function GroupLine({ group }: { group: { id: string | null; name: string | null } | null }) {
  if (group === null || group.name === null) return null;
  return <p className="post-context muted">Posted in {group.name}</p>;
}

/** A pure repost adds nothing of its own: the quiet header says whose shelf it came from. */
export function RepostedLine({ postType }: { postType: PostType }) {
  if (postType !== 'repost') return null;
  return <p className="post-context muted">Reposted — no commentary, no metrics of its own</p>;
}

/* ------------------------------------------------------- typed components */

/**
 * The link out of a component card. It is a **sibling** of the media block, never a wrapper: a
 * `MediaBlock` renders its own "Load media" button when the global toggle is off (§9.3), and a
 * button inside an anchor is both invalid markup and an ambiguous click target.
 */
function ComponentLink({ url, children }: { url: string | null; children: ReactNode }) {
  if (url === null) return <span className="post-component__link">{children}</span>;
  return (
    <a className="post-component__link" href={url} target="_blank" rel="noreferrer noopener">
      {children}
      <ExternalLinkIcon size={14} />
    </a>
  );
}

/**
 * The server-built component union (§7.4 v3). Every string here comes from the DTO; the only
 * geometry the client touches is `barPct`, which the server computed (§12.5 pt 1).
 */
export function PostComponentBlock({
  component,
  mediaEnabled,
}: {
  component: PostComponentDTO | null;
  mediaEnabled: boolean;
}) {
  if (component === null) return null;

  switch (component.kind) {
    case 'video':
      return (
        <div className="post-component post-component--video">
          <div className="post-component__stage">
            {component.thumbnail !== null ? (
              <MediaBlock
                media={component.thumbnail}
                mediaEnabled={mediaEnabled}
                alt="Video cover"
                ratio="16 / 9"
              />
            ) : (
              <span className="post-component__blank">Video</span>
            )}
            <span className="post-component__play" aria-hidden="true">
              ▶
            </span>
          </div>
          {/* The mp4 is an expiring signed URL and is never proxied — the card links to the post. */}
          <ComponentLink url={component.url}>Watch on LinkedIn</ComponentLink>
        </div>
      );

    case 'document':
      return (
        <div className="post-component post-component--document">
          {component.cover !== null ? (
            <MediaBlock
              media={component.cover}
              mediaEnabled={mediaEnabled}
              alt="Document cover"
              ratio="4 / 3"
            />
          ) : null}
          <span className="post-component__meta">
            <span className="post-component__title">{component.title ?? 'Document'}</span>
            {component.pageCountDisplay !== null ? (
              <span className="tag">{component.pageCountDisplay}</span>
            ) : null}
          </span>
          <ComponentLink url={component.url}>Open document</ComponentLink>
        </div>
      );

    case 'poll':
      return (
        <div className="post-component post-component--poll">
          {component.question !== null ? (
            <p className="post-component__title">{component.question}</p>
          ) : null}
          <ul className="poll">
            {component.options.map((option) => (
              <li className="poll__row" key={option.label}>
                <span className="poll__bar" style={{ width: `${option.barPct}%` }} aria-hidden="true" />
                <span className="poll__label">{option.label}</span>
                <span className="poll__value muted">
                  {option.pctDisplay} · {option.votesDisplay}
                </span>
              </li>
            ))}
          </ul>
          <p className="faint">
            <Num value={component.totalVotes} withTooltip={false} /> votes
            {component.statusDisplay !== null ? ` · ${component.statusDisplay}` : null}
          </p>
        </div>
      );

    case 'article':
      return (
        <div className="post-component post-component--article">
          {component.image !== null ? (
            <MediaBlock
              media={component.image}
              mediaEnabled={mediaEnabled}
              alt="Link preview"
              ratio="16 / 9"
            />
          ) : null}
          <span className="post-component__meta">
            {/* A Pulse edition is a newsletter whether or not the payload named it — the chip is
                keyed on the newsletter block existing, not on its title. */}
            {component.newsletterTitle !== null || component.newsletterUrl !== null ? (
              <span className="tag">
                {component.newsletterTitle !== null
                  ? `Newsletter: ${component.newsletterTitle}`
                  : 'Newsletter'}
              </span>
            ) : null}
            <span className="post-component__title">{component.title ?? component.url ?? 'Link'}</span>
            {component.description !== null ? (
              <span className="muted post-component__desc">{component.description}</span>
            ) : null}
            {component.domain !== null ? <span className="faint">{component.domain}</span> : null}
          </span>
          <ComponentLink url={component.url}>Open link</ComponentLink>
        </div>
      );
  }
}

export function PossiblyDeletedMarker({ possiblyDeleted }: { possiblyDeleted: boolean }) {
  if (!possiblyDeleted) return null;
  return (
    <span
      className="tag tag--warn"
      title="A re-scrape of this window no longer returned this post. The data is kept."
    >
      Possibly deleted
    </span>
  );
}

/**
 * Post body, clamped to three lines with a "See more" toggle.
 *
 * Whether the button appears is decided by measuring the clamped paragraph
 * (`scrollHeight > clientHeight`), not by a character heuristic: line count depends on the card
 * width and on where the text wraps, so a 300-character post with hard line breaks overflows
 * while a 500-character one may not. The measurement runs only while clamped — expanding removes
 * the clamp, which would otherwise report "no overflow" and hide the "See less" button.
 */
export function PostText({ text, collapsible = true }: { text: string | null; collapsible?: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const bodyRef = useRef<HTMLParagraphElement>(null);
  const clamped = collapsible && !expanded;

  useLayoutEffect(() => {
    const element = bodyRef.current;
    if (element === null || !clamped) return;
    const measure = () => {
      setOverflows(element.scrollHeight > element.clientHeight + 1);
    };
    measure();
    // Re-measure on width changes: the same text wraps to a different number of lines.
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [clamped, text]);

  if (text === null || text.length === 0) return null;

  return (
    <div className="post-text">
      <p
        ref={bodyRef}
        className={clamped ? 'post-text__body post-text__body--clamped' : 'post-text__body'}
      >
        {text}
      </p>
      {collapsible && (overflows || expanded) ? (
        <button
          type="button"
          className="btn-ghost btn-ghost--sm"
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded ? 'See less' : 'See more'}
        </button>
      ) : null}
    </div>
  );
}

/**
 * The original inside a repost/quote. Its engagement numbers are the original's **at the moment
 * this repost was scraped** — the server says so in `engagement.note`, and they are never mixed
 * into the host post's own metric row (§7.4).
 *
 * When the original is tracked here too (`postId`), the card offers to open that row instead of
 * bouncing to LinkedIn.
 *
 * `component` is the *host row's* typed component, passed only for a pure repost (Shape A): the
 * video/document/poll it carries belongs to the original, so it renders inside this frame. This is
 * presentation only — the DTO, the media roles and the mapper output are unchanged.
 */
export function EmbeddedOriginalBlock({
  embedded,
  mediaEnabled,
  onOpenPost,
  component = null,
}: {
  embedded: EmbeddedOriginalDTO | null;
  mediaEnabled: boolean;
  onOpenPost?: (postId: number) => void;
  component?: PostComponentDTO | null;
}) {
  if (embedded === null) return null;
  const engagement = embedded.engagement;
  return (
    <blockquote className="embedded">
      <div className="embedded__head">
        {embedded.authorUrl !== null ? (
          <a href={embedded.authorUrl} target="_blank" rel="noreferrer noopener">
            {embedded.authorName ?? 'Original author'}
          </a>
        ) : (
          <span className="embedded__author">{embedded.authorName ?? 'Original author'}</span>
        )}
        {embedded.authorHeadline !== null ? (
          <span className="muted embedded__headline">{embedded.authorHeadline}</span>
        ) : null}
        {embedded.publishedAtDisplay !== null ? (
          <span className="muted">{embedded.publishedAtDisplay}</span>
        ) : null}
      </div>
      <PostText text={embedded.text} />
      <MediaGallery media={embedded.media} mediaEnabled={mediaEnabled} />
      <PostComponentBlock component={component} mediaEnabled={mediaEnabled} />
      {engagement !== null ? (
        <p className="embedded__metrics faint">
          <Num value={engagement.reactions} withTooltip={false} /> reactions ·{' '}
          <Num value={engagement.comments} withTooltip={false} /> comments ·{' '}
          <Num value={engagement.shares} withTooltip={false} /> shares
          <span className="muted"> ({engagement.note})</span>
        </p>
      ) : null}
      {embedded.postId !== null && onOpenPost !== undefined ? (
        <button
          type="button"
          className="btn-ghost btn-ghost--sm embedded__link"
          onClick={() => onOpenPost(embedded.postId ?? 0)}
        >
          Open the original in this app
        </button>
      ) : embedded.url !== null ? (
        <a className="embedded__link" href={embedded.url} target="_blank" rel="noreferrer noopener">
          Open original on LinkedIn
        </a>
      ) : null}
    </blockquote>
  );
}

/**
 * The four glyph-labelled metrics, in LinkedIn's own order. Total engagement is deliberately not
 * here: no glyph reads as "total", so it keeps a text label below.
 */
const METRIC_ICONS: readonly {
  key: 'reactions' | 'comments' | 'shares' | 'saves';
  multiplierKey: 'reactionsX' | 'commentsX' | 'sharesX' | 'savesX';
  label: string;
  Icon: ComponentType<{ size?: number }>;
}[] = [
  { key: 'reactions', multiplierKey: 'reactionsX', label: 'Reactions', Icon: ThumbsUpIcon },
  { key: 'comments', multiplierKey: 'commentsX', label: 'Comments', Icon: CommentIcon },
  { key: 'shares', multiplierKey: 'sharesX', label: 'Shares', Icon: RepostIcon },
  { key: 'saves', multiplierKey: 'savesX', label: 'Saves', Icon: BookmarkIcon },
];

/**
 * Icon + number per metric, with the performance chip inline when `multipliers` is supplied (feed
 * card and post preview both pass it — the old separate multipliers row is gone). The glyph is
 * never alone: each item carries the metric name as a hover title and as screen-reader text.
 */
export function MetricsRow({
  metrics,
  multipliers,
  withTotalMultiplier = false,
}: {
  metrics: PostMetricsDTO;
  multipliers?: PostMultipliersDTO;
  /** Post preview only — the feed card stays as it was (§8.6 scope). */
  withTotalMultiplier?: boolean;
}) {
  return (
    <div className="metrics-row">
      {METRIC_ICONS.map(({ key, multiplierKey, label, Icon }) => (
        <span className="metrics-row__item" key={key} title={label}>
          <span className="metrics-row__icon">
            <Icon size={18} />
          </span>
          <span className="sr-only">{label}</span>
          <Num value={metrics[key]} withTooltip={false} />
          {multipliers !== undefined ? (
            <MultiplierBadge
              value={multipliers[multiplierKey]}
              tooltipLabel={`How ${label.toLowerCase()} × is calculated`}
            />
          ) : null}
        </span>
      ))}
      <span className="metrics-row__item">
        <span className="muted">Total</span>
        <Num value={metrics.totalEngagement} tooltipLabel="How total eng. is calculated" />
        {/* The total carries a chip too since §8.6 gave it its own baseline — it is the metric
            the Schedule performance faces are built on. */}
        {withTotalMultiplier && multipliers !== undefined ? (
          <MultiplierBadge
            value={multipliers.totalEngagementX}
            tooltipLabel="How total engagement × is calculated"
          />
        ) : null}
      </span>
    </div>
  );
}

/**
 * Height-capped frame with a bottom fade and a "View Full" action — the notification card's media
 * area (SPEC `notifications` § The notification card).
 *
 * Whether the fade appears is **measured**, never guessed (the `PostText` / `GroupTags` pattern):
 * a portrait screenshot overflows 384px where a wide one does not, and with media loading off the
 * placeholder never does. The observer watches the *inner* box, not the capped frame — the frame's
 * own height stops at the cap, so it would never report growth, while the inner box grows as each
 * image finishes decoding.
 */
export function CappedArea({
  capPx,
  actionLabel,
  onAction,
  children,
}: {
  capPx: number;
  actionLabel: string;
  onAction: () => void;
  children: ReactNode;
}) {
  const [overflows, setOverflows] = useState(false);
  const innerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const element = innerRef.current;
    if (element === null) return;
    const measure = () => {
      setOverflows(element.offsetHeight > capPx);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [capPx]);

  return (
    <div
      className={cx('media-cap', overflows && 'media-cap--capped')}
      style={overflows ? { maxHeight: `${String(capPx)}px` } : undefined}
    >
      <div ref={innerRef}>{children}</div>
      {overflows ? (
        <div className="media-cap__fade">
          <button type="button" className="btn-outline media-cap__action" onClick={onAction}>
            {actionLabel}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function PostMediaArea({
  media,
  mediaEnabled,
}: {
  media: MediaRefDTO[];
  mediaEnabled: boolean;
}) {
  return <MediaGallery media={media} mediaEnabled={mediaEnabled} />;
}
